// Implements: F-UIX-018, F-OBS-011
/**
 * The reads of the audit list, cached per organization and per filter (UIX.7, checklist 14).
 *
 * Every key carries the organization's id and every filter, so a page is only ever read back
 * under the organization and filters it was asked for: a late answer for the previous
 * organization never becomes the next one's list, and changing a filter starts again from the
 * newest row (the server's cursor is a position in one filtered list, never reused across lists).
 * "Cargar más" asks the next page of the same key. The server binds its cursor to the list it was
 * issued for and answers a cursor of another list (other filters, another organization, or one
 * issued before that binding) with a `422` `VALIDATION` (S13a-1): "Cargar más" then starts the list
 * again from its first page instead of failing (7.S13.13). The teams and the per-mockup list, which
 * name the rows, share the consumption section's keys and cache.
 */

import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import type { InfiniteData, UseInfiniteQueryResult, UseQueryResult } from "@tanstack/react-query";

import { ApiError } from "../../../shared/api/ApiError";
import type { AuditFilters, AuditTarget } from "../../../types/audit";
import type { ScopeMockupsView } from "../../../types/reports";
import { fetchAuditPage } from "../api/auditApi";
import { actionsOf, firstPagerState, nextAuditPage } from "../api/auditPager";
import type { AuditPagerState, MergedAuditPage } from "../api/auditPager";
import { fetchOrganizationTeams, fetchScopeMockups } from "../api/scopeReportsApi";
import type { TeamListView } from "../api/scopeReportsApi";
import { organizationTeamsKey, scopeReportKey } from "./useScopeReports";

export function auditKey(organizationId: string, filters: AuditFilters): readonly string[] {
  return [
    "backoffice",
    "organization",
    organizationId,
    "audit",
    filters.from,
    filters.to,
    filters.action,
    filters.teamId,
    filters.mockupId,
  ];
}

export interface AuditReads {
  readonly trail: UseInfiniteQueryResult<InfiniteData<MergedAuditPage, AuditPagerState>, ApiError>;
  /** The organization's teams, to name rows and offer the team filter. */
  readonly teams: UseQueryResult<TeamListView, ApiError>;
  /** The organization's per-mockup list, to name mockups; asked only when the server allows it. */
  readonly mockups: UseQueryResult<ScopeMockupsView, ApiError>;
  /**
   * Reads the next page; when the server refuses its cursor as one of another list, starts the
   * list again from its first page. Resolves `true` when it started again.
   */
  readonly loadMore: () => Promise<boolean>;
}

/** A refusal of a page's cursor as belonging to another list: the list must start again. */
function isOtherListCursor(error: unknown): boolean {
  return error instanceof ApiError && error.status === 422 && error.code === "VALIDATION";
}

export function useAudit(target: AuditTarget, filters: AuditFilters): AuditReads {
  const { organizationId } = target;
  const client = useQueryClient();
  const actions = actionsOf(filters.action);
  const trail = useInfiniteQuery<
    MergedAuditPage,
    ApiError,
    InfiniteData<MergedAuditPage, AuditPagerState>,
    readonly string[],
    AuditPagerState
  >({
    queryKey: auditKey(organizationId, filters),
    queryFn: ({ pageParam, signal }) =>
      nextAuditPage(pageParam, (index, cursor) =>
        fetchAuditPage(organizationId, { ...filters, action: actions[index] }, cursor, signal),
      ),
    initialPageParam: firstPagerState(actions.length),
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const teams = useQuery<TeamListView, ApiError>({
    queryKey: organizationTeamsKey(organizationId),
    queryFn: ({ signal }) => fetchOrganizationTeams(organizationId, signal),
  });
  const organization = { kind: "organization", organizationId, name: target.name } as const;
  const mockups = useQuery<ScopeMockupsView, ApiError>({
    queryKey: scopeReportKey(organization, "mockups"),
    queryFn: ({ signal }) => fetchScopeMockups(organization, signal),
    enabled: target.namesMockups,
  });
  const loadMore = async (): Promise<boolean> => {
    const result = await trail.fetchNextPage();
    if (!result.isError || !isOtherListCursor(result.error)) return false;
    // Only "Cargar más" sends a cursor, so this never fires on a first page: no restart loop. The
    // reset runs before the query cache delivers the failure to the screen (its notifier waits a
    // timer turn), so no error state is ever shown for it.
    await client.resetQueries({ queryKey: auditKey(organizationId, filters), exact: true });
    return true;
  };
  return { trail, teams, mockups, loadMore };
}
